import { useMemo, useState } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

const iso = (d) => d.toISOString().slice(0, 10);
const todayUK = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });
const COLORS = ["bg-orange-500", "bg-emerald-600", "bg-sky-600", "bg-rose-500", "bg-violet-600", "bg-amber-600", "bg-teal-600", "bg-fuchsia-600"];
export const isApproved = (t) => !!(t.enabled && t.hero_url);

function monthDays(y, m) {
  const first = new Date(Date.UTC(y, m, 1));
  const lead = (first.getUTCDay() + 6) % 7;
  const n = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return [...Array(lead).fill(null), ...Array.from({ length: n }, (_, i) => iso(new Date(Date.UTC(y, m, i + 1))))];
}

function Chip({ t, color }) {
  const go = () => document.querySelector(`[data-testid="theme-card-${t.theme_id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  return (
    <button type="button" onClick={go} title={`${t.name}${isApproved(t) ? "" : " (draft)"} · ${t.countries?.length ? `${t.countries.length} countries` : "worldwide"}`}
            className={`block w-full truncate text-left text-[10px] leading-4 px-1 rounded text-white ${color} ${isApproved(t) ? "" : "opacity-40 border border-dashed border-white"}`}
            data-testid={`cal-chip-${t.theme_id}`}>{t.name}</button>
  );
}

// Month calendar of every theme: approved solid, drafts faded, empty days tinted, overlaps flagged.
export default function SeasonalCalendar({ rows }) {
  const t0 = todayUK();
  const [ym, setYm] = useState([+t0.slice(0, 4), +t0.slice(5, 7) - 1]);
  const color = useMemo(() => Object.fromEntries((rows || []).map((t, i) => [t.theme_id, COLORS[i % COLORS.length]])), [rows]);
  const days = monthDays(ym[0], ym[1]);
  const shift = (d) => setYm(([y, m]) => [y + Math.floor((m + d) / 12), (m + d + 12) % 12]);
  const label = new Date(Date.UTC(ym[0], ym[1], 1)).toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
  return (
    <div className="card-recipe !p-5 mb-6" data-testid="seasonal-calendar">
      <div className="flex items-center gap-3 mb-3">
        <p className="font-bold text-rf-ink flex-1">Theme calendar</p>
        <button type="button" onClick={() => shift(-1)} aria-label="Previous month" data-testid="cal-prev"><CaretLeft size={18} /></button>
        <span className="font-semibold text-sm w-36 text-center" data-testid="cal-month">{label}</span>
        <button type="button" onClick={() => shift(1)} aria-label="Next month" data-testid="cal-next"><CaretRight size={18} /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-[11px]">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="font-bold text-rf-ink-soft text-center">{d}</div>)}
        {days.map((d, i) => {
          if (!d) return <div key={`b${i}`} />;
          const on = (rows || []).filter((t) => t.starts_on <= d && t.ends_on >= d);
          const live = on.filter(isApproved).length;
          return (
            <div key={d} data-testid={`cal-day-${d}`}
                 className={`min-h-[72px] rounded-md border p-1 space-y-0.5 ${!on.length ? "bg-red-50 border-red-100" : "bg-white border-slate-200"} ${d === t0 ? "ring-2 ring-rf-orange" : ""}`}>
              <div className="flex justify-between"><span className="font-bold">{+d.slice(8)}</span>
                {live > 1 && <span className="text-[9px] font-bold text-rf-orange" data-testid={`cal-overlap-${d}`}>{live} overlap</span>}</div>
              {on.slice(0, 3).map((t) => <Chip key={t.theme_id} t={t} color={color[t.theme_id]} />)}
              {on.length > 3 && <span className="text-[9px] text-rf-ink-soft">+{on.length - 3} more</span>}
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-xs text-rf-ink-soft">Solid = approved, faded = draft (not showing yet). Pink days have no theme at all. Tap a theme to jump to it.</p>
    </div>
  );
}
